import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from '../../../components/Tooltip';
import { DRAFT_NAV_BLOCKED } from '../projectSettingsDraft';
import {
    PROJECT_LIFECYCLE,
    PROJECT_LIFECYCLE_LABEL,
    normalizeProjectLifecycleStatus,
} from '../projectLifecycle';
import styles from '../ProjectEditForm.module.css';
import detailStyles from '../ProjectDetail.module.css';

/**
 * @param {{
 *   lifecycleStatus: string,
 *   hasUnsavedSettings: boolean,
 *   folderId: string,
 *   annotationUI: { id?: string },
 *   firstItemId: string | null,
 *   onRequestPublish: () => void,
 *   onRequestReturnToDraft: () => void,
 *   onRequestClose: () => void,
 *   onRequestArchive: () => void,
 *   onRequestReopen: () => void,
 *   onRequestUnarchive: () => void,
 *   onRequestDelete: () => void,
 *   publishRoiChecking?: boolean,
 *   publishPutPending?: boolean,
 *   publishPutError?: string | null,
 *   publishWarnings?: string[],
 *   publishBlocks?: string[],
 *   roiCoverageSuccessCount?: number | null,
 *   publishRoiCheckError?: string | null,
 * }} props
 */
function PublishPanel({
    lifecycleStatus,
    hasUnsavedSettings,
    folderId,
    annotationUI,
    firstItemId,
    onRequestPublish,
    onRequestReturnToDraft,
    onRequestClose,
    onRequestArchive,
    onRequestReopen,
    onRequestUnarchive,
    onRequestDelete,
    publishRoiChecking = false,
    publishPutPending = false,
    publishPutError = null,
    publishWarnings = [],
    publishBlocks = [],
    roiCoverageSuccessCount = null,
    publishRoiCheckError = null,
}) {
    const navigate = useNavigate();
    const [participantLinkCopied, setParticipantLinkCopied] = useState(false);
    const participantLinkCopyTimerRef = useRef(null);
    const busy = publishRoiChecking || publishPutPending;

    const lifecycle = normalizeProjectLifecycleStatus(lifecycleStatus);
    const isDraft = lifecycle === PROJECT_LIFECYCLE.DRAFT;
    const isPublished = lifecycle === PROJECT_LIFECYCLE.PUBLISHED;
    const isClosed = lifecycle === PROJECT_LIFECYCLE.CLOSED;
    const isArchived = lifecycle === PROJECT_LIFECYCLE.ARCHIVED;
    const shareEnabled = isPublished || isClosed;

    useEffect(() => () => {
        clearTimeout(participantLinkCopyTimerRef.current);
    }, []);

    useEffect(() => {
        if (!shareEnabled) setParticipantLinkCopied(false);
    }, [shareEnabled]);
    const navDisabled = hasUnsavedSettings;
    const canOpenItemViewer = !!(annotationUI?.id && firstItemId);

    const participantLandingUrl =
        typeof window !== 'undefined'
            ? `${window.location.origin}/projects/${folderId}/participate`
            : '';

    function handleCopyParticipantLink() {
        if (!shareEnabled) return;
        navigator.clipboard.writeText(participantLandingUrl).then(() => {
            setParticipantLinkCopied(true);
            clearTimeout(participantLinkCopyTimerRef.current);
            participantLinkCopyTimerRef.current = setTimeout(() => {
                setParticipantLinkCopied(false);
            }, 2500);
        });
    }

    const primaryIsPublish = isDraft;
    const primaryIsReturnToDraft = isPublished;
    const primaryDisabled = (!primaryIsPublish && !primaryIsReturnToDraft)
        || (primaryIsPublish && hasUnsavedSettings)
        || (primaryIsPublish && publishBlocks.length > 0)
        || busy;
    let primaryDisabledHint = '';
    if (!primaryIsPublish && !primaryIsReturnToDraft) {
        primaryDisabledHint = 'Primary action is available in draft or published status.';
    } else if (primaryIsPublish && hasUnsavedSettings) {
        primaryDisabledHint = 'Save or discard UI changes before publishing.';
    } else if (primaryIsPublish && publishBlocks.length > 0) {
        primaryDisabledHint = publishBlocks[0] || 'Fix configuration errors before publishing.';
    } else if (busy) {
        primaryDisabledHint = 'Please wait…';
    }
    const primaryButtonText = primaryIsReturnToDraft
        ? (busy ? 'Updating…' : 'Return to draft')
        : (
            publishRoiChecking
                ? 'Checking ROI coverage…'
                : publishPutPending
                    ? 'Publishing…'
                    : 'Publish project'
        );

    const secondaryCanClose = isPublished;
    const secondaryCanReopen = isClosed || isArchived;
    const secondaryDisabled = (!secondaryCanClose && !secondaryCanReopen) || busy;
    let secondaryDisabledHint = '';
    if (!secondaryCanClose && !secondaryCanReopen) {
        secondaryDisabledHint = 'Close is available for published projects; reopen is available for closed/archived projects.';
    } else if (busy) {
        secondaryDisabledHint = 'Please wait…';
    }
    const secondaryButtonText = secondaryCanReopen ? 'Reopen project' : 'Close project';

    const archiveDisabled = !isClosed || busy;

    const itemViewerTitle = !annotationUI?.id
        ? 'Select an annotation UI first'
        : !firstItemId
            ? 'Add items first'
            : 'Open viewer for all items (annotators drive progress and exports).';

    function shortcutParticipantHome() {
        navigate(`/projects/${folderId}/participate`);
    }
    function shortcutItemViewer() {
        if (canOpenItemViewer) {
            navigate(`/projects/${folderId}/view/${firstItemId}`);
        }
    }
    function shortcutProgress() {
        navigate(`/projects/${folderId}/progress`);
    }

    return (
        <div className={styles.panel}>
            <h4 className={styles.panelTitle}>Project status</h4>

            <div className={detailStyles.statusBadgeRow} aria-live="polite">
                <span
                    className={
                        isPublished
                            ? detailStyles.statusBadgePublished
                            : isClosed
                                ? detailStyles.statusBadgeClosed
                                : isArchived
                                    ? detailStyles.statusBadgeArchived
                                    : detailStyles.statusBadgeDraft
                    }
                >
                    {PROJECT_LIFECYCLE_LABEL[lifecycle]}
                </span>
                <span className={detailStyles.statusBadgeSub}>
                    {isPublished && 'Participants with a role can access and annotate.'}
                    {isDraft && 'Only project admins can access this project.'}
                    {isClosed && 'Participants can see the project but cannot annotate.'}
                    {isArchived && 'Archived projects are hidden from participant My projects.'}
                </span>
            </div>

            <div className={styles.statusShortcutsRow}>
                {navDisabled ? (
                    <Tooltip text={DRAFT_NAV_BLOCKED} position="bottom">
                        <span className={styles.inlineTooltipWrap}>
                            <button
                                type="button"
                                className={detailStyles.buttonSecondary}
                                disabled
                                aria-disabled="true"
                            >
                                Participant home
                            </button>
                        </span>
                    </Tooltip>
                ) : (
                    <button
                        type="button"
                        className={detailStyles.buttonSecondary}
                        onClick={shortcutParticipantHome}
                        title="Open the participant landing page (instructions and tasks)"
                    >
                        Participant home
                    </button>
                )}

                {navDisabled ? (
                    <Tooltip text={DRAFT_NAV_BLOCKED} position="bottom">
                        <span className={styles.inlineTooltipWrap}>
                            <button
                                type="button"
                                className={detailStyles.buttonOpen}
                                disabled
                                aria-disabled="true"
                            >
                                Project item viewer
                            </button>
                        </span>
                    </Tooltip>
                ) : (
                    <button
                        type="button"
                        className={detailStyles.buttonOpen}
                        disabled={!canOpenItemViewer}
                        onClick={shortcutItemViewer}
                        title={itemViewerTitle}
                    >
                        Project item viewer
                    </button>
                )}

                {navDisabled ? (
                    <Tooltip text={DRAFT_NAV_BLOCKED} position="bottom">
                        <span className={styles.inlineTooltipWrap}>
                            <button
                                type="button"
                                className={detailStyles.buttonSecondary}
                                disabled
                                aria-disabled="true"
                            >
                                Progress tracker
                            </button>
                        </span>
                    </Tooltip>
                ) : (
                    <button
                        type="button"
                        className={detailStyles.buttonSecondary}
                        onClick={shortcutProgress}
                        title="Open progress table for this project"
                    >
                        Progress tracker
                    </button>
                )}
            </div>

            <div
                className={`${styles.participantShareBlock} ${shareEnabled ? '' : styles.participantShareBlockDraft}`}
            >
                <h5 className={styles.participantShareHeading}>Participant link</h5>
                <p className={styles.participantShareHint}>
                    {shareEnabled
                        ? 'Share this URL with participants so they can open the project landing page directly.'
                        : 'Publish or close the project to enable sharing this link.'}
                </p>
                <div
                    className={styles.participantShareRow}
                    onCopy={(e) => {
                        if (!shareEnabled) e.preventDefault();
                    }}
                >
                    <span
                        className={`${styles.participantShareUrl} ${shareEnabled ? '' : styles.participantShareUrlDisabled}`}
                        tabIndex={shareEnabled ? undefined : -1}
                        aria-disabled={shareEnabled ? undefined : true}
                    >
                        {participantLandingUrl}
                    </span>
                    <button
                        type="button"
                        className={
                            shareEnabled
                                ? (participantLinkCopied
                                    ? styles.participantShareCopyCopied
                                    : styles.participantShareCopyBtn)
                                : styles.participantShareCopyBtn
                        }
                        onClick={handleCopyParticipantLink}
                        disabled={!shareEnabled}
                        title={
                            shareEnabled
                                ? 'Copy participant link'
                                : 'Publish or close the project to copy this link'
                        }
                    >
                        {participantLinkCopied ? '✓ Copied' : 'Copy'}
                    </button>
                </div>
            </div>

            {isDraft && !hasUnsavedSettings && publishWarnings.length > 0 && (
                <div
                    className={styles.publishWarningsBox}
                    role="region"
                    aria-labelledby="publish-warnings-heading"
                >
                    <div className={styles.publishWarningsHeader}>
                        <span className={styles.publishWarningsIcon} aria-hidden>
                            !
                        </span>
                        <strong id="publish-warnings-heading" className={styles.publishWarningsHeading}>
                            Before publishing
                        </strong>
                    </div>
                    <ul className={styles.publishWarningsList}>
                        {publishWarnings.map((w) => (
                            <li key={w} className={styles.publishWarningsItem}>
                                <span className={styles.publishWarningsItemIcon} aria-hidden>
                                    !
                                </span>
                                <span>{w}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {roiCoverageSuccessCount != null && roiCoverageSuccessCount > 0 && (
                <div className={styles.roiCoverageSuccess} role="status">
                    ROI layer present on all
                    {' '}
                    {roiCoverageSuccessCount}
                    {' '}
                    slide
                    {roiCoverageSuccessCount === 1 ? '' : 's'}
                    .
                </div>
            )}

            <div className={styles.statusActionsRow}>
                {primaryDisabled ? (
                    <Tooltip text={primaryDisabledHint || 'Action unavailable'} position="bottom">
                        <span className={styles.inlineTooltipWrap}>
                            <button
                                type="button"
                                className={styles.buttonPrimary}
                                disabled
                                aria-disabled="true"
                            >
                                {primaryButtonText}
                            </button>
                        </span>
                    </Tooltip>
                ) : (
                    <button
                        type="button"
                        className={styles.buttonPrimary}
                        onClick={primaryIsReturnToDraft ? onRequestReturnToDraft : onRequestPublish}
                        disabled={false}
                    >
                        {primaryButtonText}
                    </button>
                )}

                {secondaryDisabled ? (
                    <Tooltip
                        text={secondaryDisabledHint || 'Action unavailable'}
                        position="bottom"
                    >
                        <span className={styles.inlineTooltipWrap}>
                            <button
                                type="button"
                                className={styles.buttonSecondary}
                                disabled
                                aria-disabled="true"
                            >
                                {secondaryButtonText}
                            </button>
                        </span>
                    </Tooltip>
                ) : (
                    <button
                        type="button"
                        className={styles.buttonSecondary}
                        onClick={secondaryCanReopen ? (isArchived ? onRequestUnarchive : onRequestReopen) : onRequestClose}
                    >
                        {secondaryButtonText}
                    </button>
                )}

                {archiveDisabled ? (
                    <Tooltip
                        text={!isClosed ? 'Archive is available when project is closed.' : (busy ? 'Please wait…' : '')}
                        position="bottom"
                    >
                        <span className={styles.inlineTooltipWrap}>
                            <button
                                type="button"
                                className={styles.buttonSecondary}
                                disabled
                                aria-disabled="true"
                            >
                                Archive project
                            </button>
                        </span>
                    </Tooltip>
                ) : (
                    <button
                        type="button"
                        className={styles.buttonSecondary}
                        onClick={onRequestArchive}
                    >
                        Archive project
                    </button>
                )}

            </div>

            <div className={styles.statusActionsRow}>
                <button
                    type="button"
                    className={styles.buttonDanger}
                    onClick={onRequestDelete}
                    disabled={busy}
                    title="Delete project and all nested folders/items"
                >
                    Delete project
                </button>
            </div>

            {publishPutError && (
                <p className={styles.publishRoiCheckError} role="alert">
                    {publishPutError}
                </p>
            )}
            {publishRoiCheckError && (
                <p className={styles.publishRoiCheckError} role="alert">
                    {publishRoiCheckError}
                </p>
            )}
        </div>
    );
}

export default React.memo(PublishPanel);
